import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdSuperDirectory } from 'c1gen/features'
import { C1Chip } from 'c1ui/components/C1Chip'
import { ProfileTypeAvatar } from 'components/core/avatar/ProfileTypeAvatar'
import { useFeatureChecker } from 'components/no-ui/capability'
import { GetUserProfileTypesRequest } from 'pbts/c1/api/user/v1/user'
import { FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { getUserProfileTypesApi } from 'redux_toolkit/users'

interface UserProfileTypesProps {
    userId: string
}

export const UserProfileTypes: FC<UserProfileTypesProps> = ({ userId }) => {
    const hasSuperDirectory = useFeatureChecker(FeatureFlagIdSuperDirectory)

    const {
        data: profileTypesData,
        isLoading,
        isError,
    } = getUserProfileTypesApi.useGetUserProfileTypes(
        GetUserProfileTypesRequest.create({ userId }),
        {
            actionable: !!userId && !!hasSuperDirectory,
            notify: false, // Don't show error notifications for this API call
        },
    )

    const profileTypes = useMemo(() => {
        return profileTypesData?.profileTypes ?? []
    }, [profileTypesData])

    const visibleProfileTypes = useMemo(() => {
        return profileTypes.slice(0, 3)
    }, [profileTypes])

    const hiddenProfileTypes = useMemo(() => {
        return profileTypes.slice(3)
    }, [profileTypes])

    const tooltipContent = useMemo(() => {
        if (hiddenProfileTypes.length === 0) return null
        return (
            <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight="bold">
                    <FormattedMessage
                        id="component_user_profile_types_tooltip_header"
                        defaultMessage="Profile types"
                    />
                </Typography>
                {profileTypes.map((profileType) => (
                    <Typography key={profileType.id} variant="body2">
                        {profileType.name}
                    </Typography>
                ))}
            </Stack>
        )
    }, [profileTypes, hiddenProfileTypes.length])

    // Don't render anything if loading, error, or no profile types
    if (isLoading || isError || profileTypes.length === 0) {
        return null
    }

    return (
        <Box display="flex" flexWrap="wrap" gap={1} justifyContent="flex-start">
            {visibleProfileTypes.map((profileType) => (
                <C1Chip
                    key={profileType.id}
                    label={profileType.name}
                    size="small"
                    color="primary"
                    avatar={
                        <ProfileTypeAvatar
                            profileTypeName={profileType.name}
                            profileTypeId={profileType.id}
                            size="sm"
                        />
                    }
                    sx={{
                        paddingLeft: '1px',
                        '& .MuiChip-label': {
                            paddingLeft: '4px',
                        },
                    }}
                />
            ))}
            {hiddenProfileTypes.length > 0 && tooltipContent && (
                <Tooltip title={tooltipContent}>
                    <Box>
                        <C1Chip
                            label={
                                <FormattedMessage
                                    id="component_user_profile_types_overflow"
                                    defaultMessage="+{count}"
                                    values={{ count: hiddenProfileTypes.length }}
                                />
                            }
                            size="small"
                            color="secondary"
                            sx={{
                                backgroundColor: 'grey.100',
                                color: 'grey.700',
                                borderColor: 'border.emphasis',
                            }}
                        />
                    </Box>
                </Tooltip>
            )}
        </Box>
    )
}
